/**
 * ICOM member card numbers are stored as `{number}/{yy}` where the suffix is the
 * first-registration year (2 digits). Storage stays composite; these helpers are
 * for display, matching, and admin compose.
 */

export interface ParsedMemberCardNumber {
  raw: string;
  /** Numeric (or left-hand) card token. */
  cardNumber: string;
  /** Raw 2-digit (or longer) year suffix when present. */
  registrationYearSuffix: string | null;
  /** Expanded 4-digit first-registration year when suffix parses. */
  firstRegisteredYear: number | null;
}

const SLASH_SPLIT = /^(.+?)\/(\d{2,4})\s*$/;

/**
 * Expand a 2-digit year: yy >= 50 → 19yy, else → 20yy.
 * 3–4 digit values are returned as-is when in a plausible year range.
 */
export function expandRegistrationYearSuffix(suffix: string): number | null {
  const trimmed = suffix.trim();
  if (!/^\d{2,4}$/.test(trimmed)) return null;

  if (trimmed.length === 2) {
    const yy = parseInt(trimmed, 10);
    return yy >= 50 ? 1900 + yy : 2000 + yy;
  }

  const full = parseInt(trimmed, 10);
  if (full < 1900 || full > 2100) return null;
  return full;
}

export function parseMemberCardNumber(raw: string | null | undefined): ParsedMemberCardNumber {
  const value = (raw ?? '').trim();
  if (!value) {
    return {
      raw: '',
      cardNumber: '',
      registrationYearSuffix: null,
      firstRegisteredYear: null,
    };
  }

  const match = value.match(SLASH_SPLIT);
  if (!match) {
    return {
      raw: value,
      cardNumber: value,
      registrationYearSuffix: null,
      firstRegisteredYear: null,
    };
  }

  const cardNumber = match[1].trim();
  const registrationYearSuffix = match[2];
  return {
    raw: value,
    cardNumber: cardNumber || value,
    registrationYearSuffix,
    firstRegisteredYear: expandRegistrationYearSuffix(registrationYearSuffix),
  };
}

/** Dense one-liner for tables: `18884 · 1993` or just the card number. */
export function formatMemberCardNumberForDisplay(raw: string | null | undefined): string {
  const parsed = parseMemberCardNumber(raw);
  if (!parsed.cardNumber) return '';
  if (parsed.firstRegisteredYear != null) {
    return `${parsed.cardNumber} · ${parsed.firstRegisteredYear}`;
  }
  return parsed.cardNumber;
}

/**
 * Compose storage value. Accepts 2-digit or 4-digit year; stores 2-digit suffix
 * when a year is provided (`1993` → `93`).
 */
export function composeMemberCardNumber(
  cardNumber: string | null | undefined,
  yearOrSuffix: string | number | null | undefined
): string | undefined {
  const number = (cardNumber ?? '').trim();
  if (!number) return undefined;

  if (yearOrSuffix == null || yearOrSuffix === '') return number;

  const yearStr = String(yearOrSuffix).trim();
  if (!yearStr) return number;

  let suffix: string;
  if (/^\d{4}$/.test(yearStr)) {
    suffix = yearStr.slice(-2);
  } else if (/^\d{2}$/.test(yearStr)) {
    suffix = yearStr;
  } else {
    const expanded = expandRegistrationYearSuffix(yearStr);
    if (expanded == null) return number;
    suffix = String(expanded).slice(-2);
  }

  return `${number}/${suffix}`;
}

/** True when input card token matches stored card token (year optional on either side). */
export function memberCardNumbersMatch(
  input: string | null | undefined,
  stored: string | null | undefined
): boolean {
  const a = parseMemberCardNumber(input);
  const b = parseMemberCardNumber(stored);
  if (!a.cardNumber || !b.cardNumber) {
    const left = (input ?? '').trim().toLowerCase();
    const right = (stored ?? '').trim().toLowerCase();
    return left.length > 0 && left === right;
  }
  if (a.cardNumber.toLowerCase() !== b.cardNumber.toLowerCase()) return false;
  if (a.firstRegisteredYear != null && b.firstRegisteredYear != null) {
    return a.firstRegisteredYear === b.firstRegisteredYear;
  }
  return true;
}
